'use client';

import { useState, useRef, useEffect } from 'react';

interface Country {
  code: string;
  name: string;
  dialCode: string;
  flag: string;
}

const countries: Country[] = [
  { code: 'PA', name: 'Panamá', dialCode: '+507', flag: '🇵🇦' },
  { code: 'CO', name: 'Colombia', dialCode: '+57', flag: '🇨🇴' },
  { code: 'VE', name: 'Venezuela', dialCode: '+58', flag: '🇻🇪' },
  { code: 'ES', name: 'España', dialCode: '+34', flag: '🇪🇸' },
  { code: 'IT', name: 'Italia', dialCode: '+39', flag: '🇮🇹' },
  { code: 'CR', name: 'Costa Rica', dialCode: '+506', flag: '🇨🇷' },
  { code: 'EC', name: 'Ecuador', dialCode: '+593', flag: '🇪🇨' },
  { code: 'AR', name: 'Argentina', dialCode: '+54', flag: '🇦🇷' },
  { code: 'CL', name: 'Chile', dialCode: '+56', flag: '🇨🇱' },
  { code: 'US', name: 'Estados Unidos', dialCode: '+1', flag: '🇺🇸' },
];

interface PhoneCountrySelectorProps {
  selectedCountry: Country;
  onCountryChange: (country: Country) => void;
}

export default function PhoneCountrySelector({
  selectedCountry,
  onCountryChange,
}: PhoneCountrySelectorProps) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Cerrar dropdown al hacer click fuera
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Botón del dropdown */}
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center justify-center gap-1.5 sm:gap-2 px-2 sm:px-2.5 py-3 border border-gray-300 rounded-l-lg bg-white hover:bg-gray-50 active:bg-gray-100 transition-colors focus:outline-none focus:ring-2 focus:ring-lightbrown focus:border-transparent w-[75px] sm:w-[90px] min-h-[48px]"
        aria-label={`Seleccionar país - ${selectedCountry.name}`}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
      >
        <span 
          className="text-2xl sm:text-3xl leading-none inline-block" 
          aria-hidden="true"
          role="img"
          style={{ 
            fontFamily: 'Apple Color Emoji, Segoe UI Emoji, Segoe UI Symbol, Noto Color Emoji, emoji',
            lineHeight: '1',
            display: 'inline-block',
            fontSize: '1.5rem',
            width: '1.5rem',
            height: '1.5rem',
            textAlign: 'center',
            verticalAlign: 'middle'
          }}
        >
          {selectedCountry.flag}
        </span>
        <span className="text-xs sm:text-sm font-medium text-gray-700">
          {selectedCountry.dialCode}
        </span>
        <svg
          className={`w-3.5 h-3.5 sm:w-4 sm:h-4 text-gray-500 transition-transform duration-200 flex-shrink-0 ${
            isOpen ? 'rotate-180' : ''
          }`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          aria-hidden="true"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M19 9l-7 7-7-7"
          />
        </svg>
      </button>

      {/* Dropdown menu */}
      {isOpen && (
        <div className="absolute z-50 mt-1 w-[90px] sm:w-[110px] bg-white border border-gray-300 rounded-lg shadow-lg max-h-[300px] overflow-auto">
          <ul
            role="listbox"
            className="py-1"
            aria-label="Lista de países"
          >
            {countries.map((country) => (
              <li
                key={country.code}
                role="option"
                aria-selected={selectedCountry.code === country.code}
              >
                <button
                  type="button"
                  onClick={() => {
                    onCountryChange(country);
                    setIsOpen(false);
                  }}
                  className={`w-full flex items-center justify-center gap-2 px-2.5 sm:px-3 py-2.5 sm:py-3 hover:bg-gray-50 active:bg-gray-100 transition-colors min-h-[48px] sm:min-h-[52px] ${
                    selectedCountry.code === country.code
                      ? 'bg-gray-100'
                      : ''
                  }`}
                  aria-label={country.name}
                >
                  <span 
                    className="text-2xl sm:text-3xl leading-none flex-shrink-0 inline-block" 
                    aria-hidden="true"
                    role="img"
                    style={{ 
                      fontFamily: 'Apple Color Emoji, Segoe UI Emoji, Segoe UI Symbol, Noto Color Emoji, emoji',
                      lineHeight: '1',
                      display: 'inline-block',
                      fontSize: '1.5rem',
                      width: '1.5rem',
                      height: '1.5rem',
                      textAlign: 'center',
                      verticalAlign: 'middle'
                    }}
                  >
                    {country.flag}
                  </span>
                  <span className="text-xs sm:text-sm font-medium text-gray-700 flex-shrink-0">
                    {country.dialCode}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

